import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Dialog, DialogContent, DialogTitle } from "../components/ui/dialog";
import { Seo } from "../components/Seo";
import { Reveal } from "../components/Reveal";
import { CTABanner } from "../components/CTABanner";
import { useSite } from "../context/ContentContext";

export default function Gallery() {
  const { gallery: GALLERY } = useSite();
  const CATEGORIES = ["All", ...Array.from(new Set(GALLERY.map((g) => g.category)))];
  const [filter, setFilter] = useState("All");
  const [active, setActive] = useState(null);
  const items = filter === "All" ? GALLERY : GALLERY.filter((g) => g.category === filter);

  return (
    <div data-testid="gallery-page">
      <Seo title="Gallery" description="A professional gallery of Domaleo Enterprise operations across logistics, agriculture, IT, imports and corporate activities." />

      <section className="relative overflow-hidden pt-32 pb-20 lg:pt-40 lg:pb-28">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1606185540834-d6e7483ee1a4?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920" alt="" className="h-full w-full object-cover" />
          <div className="absolute inset-0 hero-overlay" />
        </div>
        <div className="relative container-px mx-auto max-w-7xl">
          <Reveal>
            <p className="overline text-accent">Gallery</p>
            <h1 className="mt-3 max-w-3xl text-4xl sm:text-5xl font-bold tracking-tight text-white text-balance">Our operations in pictures</h1>
            <p className="mt-5 max-w-2xl text-lg text-slate-200 leading-relaxed">A glimpse into the work we do across sectors and regions.</p>
          </Reveal>
        </div>
      </section>

      <section className="container-px mx-auto max-w-7xl py-16 lg:py-24">
        <div className="flex flex-wrap gap-3" data-testid="gallery-filters">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              onClick={() => setFilter(c)}
              data-testid={`gallery-filter-${c.replace(/[^a-z]/gi, "-").toLowerCase()}`}
              className={`rounded-full px-5 py-2 text-sm font-medium transition-all duration-200 ${
                filter === c ? "bg-primary text-primary-foreground shadow-md" : "border border-border bg-card text-foreground/80 hover:border-primary hover:text-primary"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        <motion.div layout className="mt-10 columns-1 gap-5 sm:columns-2 lg:columns-3 [&>*]:mb-5">
          <AnimatePresence>
            {items.map((g) => (
              <motion.button
                key={g.src}
                layout
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setActive(g)}
                className="group relative block w-full overflow-hidden rounded-2xl border border-border break-inside-avoid"
                data-testid="gallery-item"
              >
                <img src={g.src} alt={g.alt} className="w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <span className="absolute inset-0 flex items-end bg-gradient-to-t from-black/60 to-transparent p-4 opacity-0 transition-opacity group-hover:opacity-100">
                  <span className="text-sm font-medium text-white">{g.alt}</span>
                </span>
              </motion.button>
            ))}
          </AnimatePresence>
        </motion.div>
      </section>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-w-3xl p-0 overflow-hidden border-none bg-transparent">
          <DialogTitle className="sr-only">{active?.alt || "Gallery image"}</DialogTitle>
          {active && <img src={active.src} alt={active.alt} className="w-full rounded-xl" />}
        </DialogContent>
      </Dialog>

      <CTABanner />
    </div>
  );
}
